// src/pages/Signup.jsx
import { useState } from "react";
import usePageMeta from "../hooks/usePageMeta";
import { Link, useNavigate } from "react-router-dom";
import API from "../api";
import { showError, showSuccess } from "../utils/dialog";
import { isValidEmail } from "../utils/email";
import "../App.css";
import talentLogo2 from "../assets/talentlogo2.png";

export default function Signup() {
  usePageMeta("Signup");
  const STRICT_VALIDATION = true;
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName]   = useState("");
  const [email, setEmail]         = useState("");
  const [password, setPassword]   = useState("");
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validatePassword = (val) => {
    const requirements = [
      { test: (v) => v.length >= 8, msg: "At least 8 characters" },
      { test: (v) => /[a-z]/.test(v), msg: "At least one lowercase letter" },
      { test: (v) => /[A-Z]/.test(v), msg: "At least one uppercase letter" },
      { test: (v) => /\d/.test(v), msg: "At least one number" },
      { test: (v) => /[^A-Za-z0-9]/.test(v), msg: "At least one special character" },
    ];
    const unmet = requirements.filter(r => !r.test(val)).map(r => r.msg);
    setIsPasswordValid(unmet.length === 0);
    setPasswordError(unmet.length ? `Password must include: ${unmet[0]}` : "");

    return unmet.length === 0;
  };

  // Pure helper for render-time validation without setting state
  const isPasswordStrong = (val) => {
    if (!val) return false;
    return (
      val.length >= 8 &&
      /[a-z]/.test(val) &&
      /[A-Z]/.test(val) &&
      /\d/.test(val) &&
      /[^A-Za-z0-9]/.test(val)
    );
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      showError("Please enter your first and last name");
      return;
    }
    if (!isValidEmail(email)) {
      showError("Please enter a valid email address");
      return;
    }
    if (!validatePassword(password)) {
      showError(passwordError || "Please enter a stronger password");
      return;
    }
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const res = await API.post("/auth/signup", {
        first_name: firstName,
        last_name: lastName,
        email,
        password,
      });
      console.log(res.data);
      showSuccess("Signup successful!");
      // Persist signup values locally so registration items can prefill before login
      try {
        localStorage.setItem('signup_first_name', firstName);
        localStorage.setItem('signup_last_name', lastName);
        localStorage.setItem('signup_email', email);
      } catch (e) {
        // ignore storage errors
      }
      navigate("/login");
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || "Signup failed";
      showError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handlePasswordChange = (e) => {
    const val = e.target.value;
    setPassword(val);
    validatePassword(val);
  };

  // Field validity computed on each render
  const isFirstValid = firstName.trim().length > 0;
  const isLastValid = lastName.trim().length > 0;
  const isEmailValid = isValidEmail(email);
  const isPwdValid = isPasswordStrong(password);


  return (
    <div className="auth-container">
      <div className="background-overlay"></div>

      <div className="form-container">
        <div className="form-card">
          <div className="atlas-logo">
            <div className="flex items-start gap-6">
              <img src={talentLogo2} alt="Talent Logo" className="h-14 w-auto" />
            </div>
          </div>

          <h1 className="form-title">Sign Up</h1>

          <form className="auth-form" onSubmit={handleSignup}>
            <div className="form-group">
              <label className="form-label">First name*</label>
              <input
                type="text"
                className="form-input"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Last name*</label>
              <input
                type="text"
                className="form-input"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Email*</label>
              <input
                type="email"
                className="form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Password*</label>
              <div className="password-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  className="form-input"
                  value={password}
                  onChange={handlePasswordChange}
                  autoComplete="new-password"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? "🙈" : "👁️"}
                </button>
              </div>
              {passwordError && <p className="error-text">{passwordError}</p>}
              {/* <p className="password-hint">Make sure it&apos;s 8 characters or more</p> */}
            </div>

            <button
              type="submit"
              className={`submit-button ${isSubmitting ? "loading" : ""}`}
              disabled={!(isFirstValid && isLastValid && isEmailValid && isPwdValid) || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner" />
                  Processing...
                </>
              ) : (
                "Create Account"
              )}
            </button>
          </form>

          <p className="form-footer">
            Back to <Link className="toggle-form" to="/login">Sign In</Link>
          </p>
          

        </div>
      </div>
    </div>
  );
}
